/**
 * PreflightBanner — Owner-only Admin dashboard banner.
 *
 * Renders the last Deploy Preflight verdict + timestamp with a subtle pulse
 * when the last run failed OR is older than 24 hours. Non-owner accounts
 * never see it. Links straight to /admin/deploy-preflight.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Rocket, Clock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function fmtWhen(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("en-AU", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function PreflightBanner() {
  const { user } = useAuth();
  const [state, setState] = useState(null);   // {status, checked_at, age_hours, stale, failed_checks}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_owner) return;
    let cancelled = false;
    api.get("/admin/deploy-preflight/last")
      .then((r) => { if (!cancelled) setState(r.data); })
      .catch(() => { if (!cancelled) setState({ status: null, stale: true }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  if (!user?.is_owner || loading) return null;

  const isPass = state?.status === "PASS" && !state?.stale;
  const isFail = state?.status === "FAIL";
  const noRun = !state?.checked_at;
  const stale = state?.stale && !isFail && !noRun;

  const commonClasses =
    "flex items-center justify-between gap-4 px-4 sm:px-6 py-3 border mb-4 flex-wrap";

  if (isPass) {
    return (
      <Link
        to="/admin/deploy-preflight"
        data-testid="preflight-banner-pass"
        className={`${commonClasses} border-emerald-300 bg-emerald-50 hover:border-emerald-400 transition-colors`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-emerald-900">
              🟢 Last Preflight: PASS
            </div>
            <div className="text-xs text-emerald-700">
              Completed {fmtWhen(state.checked_at)} · {state.age_hours != null ? `${state.age_hours}h ago` : ""}
            </div>
          </div>
        </div>
        <span className="text-xs text-emerald-800 hover:text-emerald-900 underline whitespace-nowrap">
          Open Deploy Preflight →
        </span>
      </Link>
    );
  }

  // Warning states — pulse the icon when action needed.
  const label = noRun
    ? "🔴 Preflight Required — no successful preflight recorded yet"
    : isFail
      ? `🔴 Preflight Required — last run FAILED (${(state.failed_checks || []).join(", ") || "unknown checks"})`
      : `🔴 Preflight Required — last run is ${state.age_hours ?? "?"}h old`;

  return (
    <Link
      to="/admin/deploy-preflight"
      data-testid="preflight-banner-required"
      className={`${commonClasses} border-rose-300 bg-rose-50 hover:border-rose-400 transition-colors`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 animate-pulse" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-rose-900">{label}</div>
          <div className="text-xs text-rose-700 flex items-center gap-2 mt-0.5">
            {state.checked_at ? (
              <><Clock className="w-3 h-3" /> Last checked {fmtWhen(state.checked_at)}</>
            ) : (
              <>No preflight recorded yet</>
            )}
            {stale && <span>· stale after 24h</span>}
          </div>
        </div>
      </div>
      <span className="inline-flex items-center gap-1 px-3 h-8 bg-rose-600 text-white text-xs font-medium hover:bg-rose-700 whitespace-nowrap">
        <Rocket className="w-3.5 h-3.5" /> Run preflight
      </span>
    </Link>
  );
}
